import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, mergeMap, Observable, take, tap } from 'rxjs';
import type { Policy, PolicyRole } from '../models/policy.model';
import { PolicyApiPort } from '../ports/policy-api.port';

@Injectable({
  providedIn: 'root'
})
export class PolicyService {
  readonly policyApiPort = inject(PolicyApiPort);
  policiesSubject: BehaviorSubject<Policy[]> = new BehaviorSubject<Policy[]>([]);
  readonly policies$: Observable<Policy[]> = this.policiesSubject.asObservable();

  constructor() {
    this.loadPolicies();
  }

  private loadPolicies() {
    this.policyApiPort
      .getPolicies()
      .pipe(take(1))
      .subscribe((policies) => {
        this.policiesSubject.next(policies);
      });
  }

  public reloadPolicies(): Observable<Policy[]> {
    return this.policyApiPort.getPolicies().pipe(
      tap((policies: Policy[]) => {
        this.policiesSubject.next(policies);
      })
    );
  }

  createPolicy(policy: Policy, role?: PolicyRole): Observable<{ policyId: string }> {
    return this.policyApiPort.sendPolicy(policy, role).pipe(
      take(1),
      mergeMap((result) => this.reloadPolicies().pipe(map(() => result)))
    );
  }

  editPolicy(id: string, policy: Policy, role?: PolicyRole): Observable<void> {
    return this.policyApiPort.updatePolicy(id, policy, role).pipe(
      take(1),
      mergeMap(() => this.reloadPolicies()),
      map(() => undefined)
    );
  }

  deletePolicy(id: string) {
    return this.policyApiPort.deletePolicy(id);
  }

  getPolicies$() {
    return this.policies$;
  }

  getPolicyById(id: string): Policy | undefined {
    return this.policiesSubject.getValue().find((p) => p.id === id);
  }

  getPoliciesSnapshot(): Policy[] {
    return this.policiesSubject.getValue();
  }
}
